import { IPlaybook } from '@forwardflow/features/campaign-flow/interfaces/campaign-flow.types'
import { spokenLines } from '@forwardflow/features/campaign-flow/utils/playbook'

/**
 * The live campaign speaks with the catalog's voices (Gemini). The test call
 * in the browser runs on ElevenLabs' conversational agent, which only speaks
 * its own voices, so a stand-in of the same gender is used; the Voice step's
 * recording is the real one.
 */
const ELEVEN_FEMALE =
   'hpp4J3VqNfWAUOO0d1Us' /* Bella: professional, bright, warm */
const ELEVEN_MALE = 'cjVigY5qzO86Huf0OWal' /* Eric: smooth, trustworthy */
export const standInVoiceFor = (gender?: string | null, name?: string) =>
   /^m/i.test(gender || '') ||
   /^(jonathan|john|james|michael|david|eric|sam)$/i.test(name || '')
      ? ELEVEN_MALE
      : ELEVEN_FEMALE

/**
 * Engine tokens the library's sections use that the screens do not map to
 * chips; the test call fills them with the sample lead, so the agent never
 * reads a bare `{property_info}`.
 */
const sampleTokens = (o: {
   leadFirstName: string
   city: string
   source: string
   assistantName: string
   agentName: string
}): Record<string, string> => ({
   lead_firstname: o.leadFirstName,
   lead_first_name: o.leadFirstName,
   lead_email: `${o.leadFirstName.toLowerCase()}@example.com`,
   lead_description: `Looking at homes near ${o.city}.`,
   lead_source: o.source,
   lead_timeline_summary: 'Started looking this month.',
   lead_message_history: '[]',
   lead_question: '',
   agent_name: o.agentName,
   agent_firstname: o.agentName.split(' ')[0],
   agent_phone: '',
   organization_name: 'our brokerage',
   brokerage_name: 'our brokerage',
   team_name: 'our team',
   assistant_name: o.assistantName,
   assistant_intro: `this is ${o.assistantName}`,
   campaign_goal: 'book a quick call with the agent',
   specific_features: '',
   qualifying_questions: '',
   usps: '',
   property_info: `a home near ${o.city}`,
   short_property_info: `a home near ${o.city}`,
   street_name: 'Main Street',
   street_number: '12',
   street: '12 Main Street',
   calendar_availabilities: 'Tuesday 2pm or Wednesday 11am',
   lender: '',
   appointment_date_time: '',
})

/**
 * The prompt the test call runs on: the playbook's sections word for word
 * (variables filled for the sample lead), told to open with the opening line.
 * `fill` is the screen's own filler for `{first name}`-style chips; engine
 * tokens left over are filled here.
 */
export const callPrompt = (
   pb: IPlaybook,
   fill: (text: string) => string,
   sample: Parameters<typeof sampleTokens>[0]
): string => {
   const tokens = sampleTokens(sample)
   const filled = (text: string) =>
      fill(text).replace(/\{([a-z_]+)\}/g, (m, k: string) =>
         k in tokens ? tokens[k] : m
      )
   const opening = filled(spokenLines(pb)[0] ?? '')
   const sections = pb.script
      .filter((s) => s.text.trim())
      .map((s) => `## ${s.title}\n${filled(s.text)}`)
      .join('\n\n')
   return [
      `This is a test call. You are ${sample.assistantName}, an assistant calling ${sample.leadFirstName}, a ${sample.source} lead looking near ${sample.city}. Speak naturally and briefly, one question at a time, as on a real phone call.`,
      `Open the call by saying: "${opening}"`,
      'Then follow this script:',
      sections,
   ].join('\n\n')
}
